"use client";

import { useMemo, useState } from "react";
import { Link } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    DELAI_REPONSE_JOURS_OUVRES,
    activitesRemplies,
    composerTableau,
    concerne,
    partLisible,
    recapitulatif,
    totalPourcentages,
    valider,
} from "@/lib/tools/apeCodeReview";
import { documentEnTexte, nomDuFichier } from "@/lib/tools/documentText";
import { telechargerDocument } from "@/lib/tools/documentPdf";
import { ApercuDocument } from "./DocumentPaper";
import { PadSignature, useSignature } from "./SignaturePad";
import {
    Avancer,
    Champ,
    Choix,
    Ecran,
    Recommencer,
    Resultat,
    remplir,
    useParcours,
} from "./Journey";
import { useDossierOutil, useSignalerIdentite } from "./ToolsDossier";

/**
 * La révision du code APE, et le tableau d'activité qu'elle exige.
 *
 * LE TRI ÉCARTE DEUX DEMANDEURS SUR TROIS, et c'est le service qu'il rend. Le
 * code APE ne se corrige à l'INSEE que s'il n'a jamais correspondu ; une
 * activité qui a changé se déclare au guichet unique, et l'INSEE en tire le
 * code tout seul. Quant à celui que son libellé dérange sans plus, on lui
 * rappelle que ce code n'a aucune valeur juridique avant de lui faire remplir
 * un tableau.
 *
 * LE TABLEAU SE RECONSTRUIT, il ne se remplit pas : l'INSEE publie le sien en
 * PDF sans champs. Le document produit reprend ses colonnes, ses intitulés et
 * ses mentions, et le parcours applique les règles de sa notice, qui décident
 * si le dossier sera étudié ou renvoyé.
 */

const L = {
    fr: {
        phases: ["Votre demande", "Vos activités", "Signature"],
        retour: "Retour",
        prive: "Tout se fait sur votre appareil : le tableau s'y fabrique, personne ne reçoit vos réponses, pas même nous, et tout disparaît avec l'onglet.",
        motifTitre: "Pourquoi votre code APE ne va-t-il pas ?",
        motifAide: "Selon votre réponse, ce n'est pas au même endroit qu'il faut s'adresser.",
        motifErreur: "Il n'a jamais correspondu",
        motifErreurDetail: "Une erreur de codification dès le départ",
        motifChangement: "Mon activité a changé depuis",
        motifChangementDetail: "Je fais autre chose qu'au début",
        motifGout: "Son libellé me dérange",
        motifGoutDetail: "Sans que mon activité ait changé",
        horsSujetTitre: "Ce n'est pas ici que ça se règle",
        motifRefusChangement: "Un changement d'activité se déclare au guichet unique, et l'INSEE attribue alors le nouveau code d'après cette déclaration, sans que vous ayez à le demander : un tableau d'activité ne ferait rien avancer. Avant de vous lancer, vérifiez que le changement est réel et durable : la notice n'en retient pas d'autre.",
        motifRefusGout: "Le code APE n'a aucune valeur juridique : votre catégorie fiscale, votre taux de cotisations et votre caisse de retraite n'en dépendent pas, et personne ne peut vous refuser un droit en s'appuyant dessus. Si son libellé vous dessert auprès d'un client ou d'une banque, vous avez raison de vouloir le changer. Mais l'INSEE ne le corrigera que s'il ne correspondait pas à votre activité dès le premier jour.",
        versGlossaire: "À quoi sert vraiment le code APE",
        sirenTitre: "Le numéro SIREN de votre entreprise",
        sirenAide: "Neuf chiffres, sur votre avis de situation Sirene. C'est par lui que l'INSEE retrouve votre dossier.",
        champSiren: "Numéro SIREN",
        activitesTitre: "Vos activités, une par ligne",
        activitesAide: "Commencez par votre activité principale. Une ligne par activité, décrite avec vos mots, sans code ni nom de métier, et un total qui fait exactement 100 %.",
        champDesignation: "Désignation de l'activité",
        placeholderDesignation: "Ce que vous faites, en clair",
        champPart: "Part du CA",
        champEffectif: "Salariés",
        ajouterActivite: "Ajouter une activité",
        retirerActivite: "Retirer",
        totalJuste: "Total : 100 %, le tableau est complet.",
        totalFaux: "Le total doit faire exactement 100 %. Il en manque {ecart} %.",
        totalTrop: "Le total dépasse 100 % de {ecart} %.",
        contactTitre: "Qui répond pour l'entreprise ?",
        contactAide: "Le tableau demande le nom, la fonction et les coordonnées de la personne qui le remplit. L'INSEE répond par courriel.",
        champPrenoms: "Prénoms",
        champNom: "Nom",
        champFonction: "Fonction",
        aideFonction: "Pour une micro-entreprise, « micro-entrepreneur » ou « gérant » convient.",
        champMail: "Adresse courriel",
        champTelephone: "Numéro de téléphone",
        aideTelephone: "Facultatif.",
        signatureTitre: "Où et quand signez-vous ?",
        signatureAide: "Le tableau doit être signé. Il demande aussi le cachet de l'entreprise, mais peu de micro-entrepreneurs en ont un : votre signature et vos coordonnées suffisent.",
        champLieuSignature: "Lieu de signature",
        champDateSignature: "Date de signature",
        signatureDessin: "Votre signature",
        signatureDessinAide: "Facultative. Tracez-la à la souris ou au doigt, ou laissez l'emplacement vide pour signer à la main après impression. Elle sera reprise par vos autres documents de la visite.",
        signatureReprise: "Reprise de votre document précédent. Retracez-la par-dessus ou effacez-la si ce n'est pas la bonne.",
        effacer: "Effacer",
        continuer: "Continuer",
        voirDocument: "Voir mon tableau",
        resultatTitre: "Votre tableau d'activité",
        recapTitre: "Ce que vous déclarez",
        apercu: "Aperçu du document",
        telecharger: "Télécharger en PDF",
        copier: "Copier le texte",
        copie: "Texte copié",
        recommencer: "Recommencer",
        manquesTitre: "Il manque encore quelque chose",
        manquesAide: "Complétez les champs signalés pour obtenir votre tableau.",
        verifierTitre: "Une valeur à vérifier",
        verifierAide: "Ces champs sont remplis, mais il y a un problème dans ce qu'ils contiennent. Corrigez-les, puis revenez ici.",
        erreurFormat: "Ce format n'est pas reconnu.",
        erreurSiren: "Un SIREN compte neuf chiffres. Le vôtre en a {compte}. Attention à ne pas donner votre SIRET, qui en compte quatorze.",
        erreurTotal: "Le total des pourcentages fait {total} % au lieu de 100 %.",
        erreurCodeNaf: "La ligne {rang} contient un code d'activité. La notice de l'INSEE demande de n'en indiquer aucun : décrivez l'activité avec vos mots.",
        erreurDesignation: "La ligne {rang} n'a pas de désignation.",
        erreurPart: "La ligne {rang} n'a pas de pourcentage valable, entre 1 et 100.",
        alerteRegroupement: "Une de vos lignes réunit peut-être deux activités, comme « fabrication et installation ». L'INSEE en demande une par ligne. Relisez-la avant d'envoyer.",
        alerteUneSeule: "Vous ne déclarez qu'une activité, à 100 %. C'est accepté. Mais si vous en exercez d'autres, les indiquer aide l'INSEE à choisir la bonne activité principale.",
        suiteTitre: "Et maintenant",
        suite1: "Allez sur insee.fr, ouvrez le formulaire de demande de révision du code APE, et joignez-y ce tableau.",
        suite2: "La réponse arrive par courriel sous {delai} jours ouvrés, avec les raisons de la décision, favorable ou non.",
        suite3: "La démarche est gratuite, et l'INSEE ne passe par aucun intermédiaire : inutile de payer un site pour la faire à votre place.",
        fondTitre: "Vos cotisations ne bougeront pas",
        fond: "Elles se calculent sur le chiffre d'affaires que vous déclarez, jamais sur le code inscrit sur votre avis de situation. Ce code sert surtout à vos clients, à votre banque et à votre assureur, qui le lisent pour savoir ce que vous faites.",
    },
    en: {
        phases: ["Your request", "Your activities", "Signature"],
        retour: "Back",
        prive: "Everything happens on your device: the table is built there, nobody receives your answers, not even us, and it all disappears with the tab.",
        motifTitre: "What is wrong with your APE code?",
        motifAide: "Depending on your answer, you will not be writing to the same place.",
        motifErreur: "It never matched",
        motifErreurDetail: "A coding error from the start",
        motifChangement: "My activity has changed since",
        motifChangementDetail: "I do something else now",
        motifGout: "Its wording bothers me",
        motifGoutDetail: "Though my activity has not changed",
        horsSujetTitre: "This is not where it gets settled",
        motifRefusChangement: "A change of activity is declared to the single window, and INSEE then assigns the new code from that declaration, without you having to ask: an activity table would get you nowhere. Before you start, check that the change is real and lasting: the notice accepts no other kind.",
        motifRefusGout: "The APE code has no legal force: your tax category, your contribution rate and your pension fund do not depend on it, and nobody can refuse you a right on the strength of it. If its wording puts you in a bad light with a client or a bank, you are right to want it changed. But INSEE will only correct it if it did not match your activity from day one.",
        versGlossaire: "What the APE code is really for",
        sirenTitre: "Your company's SIREN number",
        sirenAide: "Nine digits, on your SIRENE situation notice. It is how INSEE finds your file.",
        champSiren: "SIREN number",
        activitesTitre: "Your activities, one per line",
        activitesAide: "Start with your main activity. One line per activity, described in your own words, with no code and no job title, and a total of exactly 100%.",
        champDesignation: "Description of the activity",
        placeholderDesignation: "What you do, in plain words",
        champPart: "Share of turnover",
        champEffectif: "Employees",
        ajouterActivite: "Add an activity",
        retirerActivite: "Remove",
        totalJuste: "Total: 100%, the table is complete.",
        totalFaux: "The total must be exactly 100%. {ecart}% is missing.",
        totalTrop: "The total exceeds 100% by {ecart}%.",
        contactTitre: "Who answers for the company?",
        contactAide: "The table asks for the name, role and contact details of whoever fills it in. INSEE replies by email.",
        champPrenoms: "Forenames",
        champNom: "Surname",
        champFonction: "Role",
        aideFonction: "For a micro-business, « micro-entrepreneur » or « owner » will do.",
        champMail: "Email address",
        champTelephone: "Telephone number",
        aideTelephone: "Optional.",
        signatureTitre: "Where and when are you signing?",
        signatureAide: "The table has to be signed. It also asks for the company stamp, but few micro-entrepreneurs have one: your signature and your contact details are enough.",
        champLieuSignature: "Place of signature",
        champDateSignature: "Date of signature",
        signatureDessin: "Your signature",
        signatureDessinAide: "Optional. Draw it with the mouse or your finger, or leave the space empty and sign by hand after printing. Your other documents in this visit will reuse it.",
        signatureReprise: "Taken from your previous document. Draw over it, or clear it if it is not the right one.",
        effacer: "Clear",
        continuer: "Continue",
        voirDocument: "See my table",
        resultatTitre: "Your activity table",
        recapTitre: "What you are declaring",
        apercu: "Document preview",
        telecharger: "Download as PDF",
        copier: "Copy the text",
        copie: "Text copied",
        recommencer: "Start again",
        manquesTitre: "Something is still missing",
        manquesAide: "Fill in the highlighted fields to get your table.",
        verifierTitre: "Something to check",
        verifierAide: "These fields are filled in, but there is a problem with what they contain. Correct them, then come back here.",
        erreurFormat: "This format is not recognised.",
        erreurSiren: "A SIREN has nine digits. Yours has {compte}. Take care not to give your SIRET, which has fourteen.",
        erreurTotal: "The percentages add up to {total}% instead of 100%.",
        erreurCodeNaf: "Line {rang} contains an activity code. INSEE's notice asks for none: describe the activity in your own words.",
        erreurDesignation: "Line {rang} has no description.",
        erreurPart: "Line {rang} has no valid percentage, between 1 and 100.",
        alerteRegroupement: "One of your lines may be combining two activities, such as « manufacture and installation ». INSEE wants one per line. Read it again before sending.",
        alerteUneSeule: "You are declaring a single activity, at 100%. That is accepted. But if you carry out others, listing them helps INSEE pick the right main activity.",
        suiteTitre: "What happens next",
        suite1: "Go to insee.fr, open the APE code revision request form, and attach this table to it.",
        suite2: "The reply comes by email within {delai} working days, with the reasons for the decision, favourable or not.",
        suite3: "The procedure is free, and INSEE works through no intermediary: there is no need to pay a website to do it for you.",
        fondTitre: "Your contributions will not change",
        fond: "They are calculated on the turnover you declare, never on the code written on your situation notice. The code matters mostly to your clients, your bank and your insurer, who read it to see what you do.",
    },
};

const PHASES_PAR_ECRAN = {
    motif: 0,
    horsSujet: 0,
    siren: 0,
    activites: 1,
    contact: 1,
    signature: 2,
    resultat: 2,
};

const LIGNE_VIDE = { designation: "", effectif: "0", part: "" };

const VIDE = {
    motif: "",
    siren: "",
    activites: [{ ...LIGNE_VIDE }, { ...LIGNE_VIDE }],
    prenoms: "",
    nom: "",
    fonction: "",
    mail: "",
    telephone: "",
    lieuDeSignature: "",
    dateDeSignature: "",
};

/** Les libellés des champs, hors du dictionnaire lu par le contrôle de parité. */
const LIBELLES_MANQUES = {
    fr: {
        motif: "Le motif de votre demande",
        siren: "Votre numéro SIREN",
        activites: "Au moins une activité",
        prenoms: "Vos prénoms",
        nom: "Votre nom",
        fonction: "Votre fonction",
        mail: "Votre adresse courriel",
        lieuDeSignature: "Le lieu de signature",
        dateDeSignature: "La date de signature",
    },
    en: {
        motif: "The reason for your request",
        siren: "Your SIREN number",
        activites: "At least one activity",
        prenoms: "Your forenames",
        nom: "Your surname",
        fonction: "Your role",
        mail: "Your email address",
        lieuDeSignature: "The place of signature",
        dateDeSignature: "The date of signature",
    },
};

export default function RevisionCodeApe({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    const { identite } = useDossierOutil();
    const [r, setR] = useState(() => ({ ...VIDE, ...identite }));
    const [copie, setCopie] = useState(false);
    const { signature, poser: poserSignature } = useSignature();

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "motif",
        () => {
            setR({ ...VIDE, ...identite, activites: [{ ...LIGNE_VIDE }, { ...LIGNE_VIDE }] });
            setCopie(false);
        },
        PHASES_PAR_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    useSignalerIdentite(
        { prenoms: r.prenoms, nom: r.nom, mail: r.mail, telephone: r.telephone },
        etape === "signature" || etape === "resultat",
    );

    const poser = (cle) => (valeur) => setR((p) => ({ ...p, [cle]: valeur }));

    /* Le tableau se modifie ligne à ligne : chaque cellule remplace la sienne
       sans toucher aux autres, et l'ajout se fait en fin de liste. */
    const poserCellule = (rangee, colonne) => (valeur) =>
        setR((p) => ({
            ...p,
            activites: p.activites.map((a, i) => (i === rangee ? { ...a, [colonne]: valeur } : a)),
        }));
    const ajouterLigne = () => setR((p) => ({ ...p, activites: [...p.activites, { ...LIGNE_VIDE }] }));
    const retirerLigne = (rangee) =>
        setR((p) => ({ ...p, activites: p.activites.filter((_, i) => i !== rangee) }));

    const jour = String(aujourdhui).slice(0, 10);
    const reponses = useMemo(
        () => ({ ...r, dateDeSignature: r.dateDeSignature || jour }),
        [r, jour],
    );

    const controle = useMemo(() => valider(reponses), [reponses]);
    const declaration = useMemo(
        () => composerTableau(reponses, aujourdhui),
        [reponses, aujourdhui],
    );
    const recap = useMemo(() => recapitulatif(reponses, aujourdhui), [reponses, aujourdhui]);
    const rempli = (...cles) => cles.every((c) => String(r[c] || "").trim());

    const refusDe = (champ) => (controle.invalides || []).find((i) => i.champ === champ);
    const erreurDe = (champ) => {
        const refus = refusDe(champ);
        if (!refus) return "";
        if (champ === "siren") {
            return remplir(t.erreurSiren, { compte: String(r.siren || "").replace(/\D/g, "").length });
        }
        return t.erreurFormat;
    };

    /* Les refus du tableau se disent en clair, avec le numéro de la ligne : un
       message qui ne nomme pas sa ligne oblige à toutes les relire. */
    const refusDuTableau = (controle.invalides || [])
        .filter((i) => i.champ === "activites")
        .map((i) => {
            if (i.motif === "total") return remplir(t.erreurTotal, { total: i.total });
            if (i.motif === "codeNaf") return remplir(t.erreurCodeNaf, { rang: i.rang + 1 });
            if (i.motif === "designationVide") return remplir(t.erreurDesignation, { rang: i.rang + 1 });
            return remplir(t.erreurPart, { rang: i.rang + 1 });
        });

    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };
    const tri = concerne(r);
    const total = totalPourcentages(activitesRemplies(r.activites));

    const telecharger = () =>
        telechargerDocument({
            document: declaration,
            signature,
            nomFichier: nomDuFichier("tableau-activite-ape", r),
        });

    const copier = async () => {
        if (!declaration) return;
        try {
            await navigator.clipboard.writeText(documentEnTexte(declaration));
            setCopie(true);
            window.setTimeout(() => setCopie(false), 2000);
        } catch {
            // Presse-papiers refusé : le PDF reste la voie normale.
        }
    };

    const slugGlossaire = lang === "en" ? "french-micro-enterprise" : "micro-entreprise";

    /* ---------------------------------------------------------------- */
    /* Les écrans                                                        */
    /* ---------------------------------------------------------------- */

    if (etape === "motif") {
        return (
            <Ecran key={etape} {...commun} phase={0} titre={t.motifTitre} aide={t.motifAide}>
                <div className="grid sm:grid-cols-3 gap-3">
                    {[
                        { cle: "erreurCodification", icone: "close-circle-linear", titre: t.motifErreur, detail: t.motifErreurDetail },
                        { cle: "changementActivite", icone: "calendar-linear", titre: t.motifChangement, detail: t.motifChangementDetail },
                        { cle: "simpleGout", icone: "info-circle-linear", titre: t.motifGout, detail: t.motifGoutDetail },
                    ].map((c) => (
                        <Choix
                            key={c.cle}
                            icone={c.icone}
                            titre={c.titre}
                            detail={c.detail}
                            onClick={() => {
                                setR((p) => ({ ...p, motif: c.cle }));
                                aller(c.cle === "erreurCodification" ? "siren" : "horsSujet");
                            }}
                        />
                    ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
            </Ecran>
        );
    }

    if (etape === "horsSujet") {
        return (
            <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.horsSujetTitre} phases={t.phases} sens={sens} reduit={reduit}>
                <div className="mt-4">
                    <p className="text-[15px] leading-7 text-gray-600 dark:text-lightBlue mb-0">
                        {tri.motif === "changementActivite" && t.motifRefusChangement}
                        {tri.motif === "simpleGout" && t.motifRefusGout}
                    </p>
                    <Link
                        href={{
                            pathname: "/glossaries/[slug]/[termSlug]",
                            params: { slug: slugGlossaire, termSlug: "code-ape" },
                        }}
                        className="inline-block mt-5 text-sm font-semibold text-primary hover:!text-primary"
                    >
                        {t.versGlossaire} →
                    </Link>
                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            </Resultat>
        );
    }

    if (etape === "siren") {
        return (
            <Ecran key={etape} {...commun} phase={0} onRetour={retour} titre={t.sirenTitre} aide={t.sirenAide}>
                <Champ
                    libelle={t.champSiren}
                    valeur={r.siren}
                    poser={poser("siren")}
                    erreur={erreurDe("siren")}
                    inputMode="numeric"
                />
                <Avancer
                    pleineLargeur
                    disabled={!rempli("siren") || Boolean(refusDe("siren"))}
                    onClick={() => aller("activites")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "activites") {
        const ecart = Math.round(Math.abs(100 - total) * 10) / 10;
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={1}
                onRetour={retour}
                titre={t.activitesTitre}
                aide={t.activitesAide}
            >
                <div className="space-y-4">
                    {r.activites.map((activite, i) => (
                        <div
                            key={`ligne-${i}`}
                            className="rounded-xl border border-gray-200/70 dark:border-white/10 p-4"
                        >
                            <div className="grid sm:grid-cols-6 gap-3">
                                <div className="sm:col-span-4">
                                    <Champ
                                        libelle={`${t.champDesignation} ${i + 1}`}
                                        valeur={activite.designation}
                                        poser={poserCellule(i, "designation")}
                                        placeholder={t.placeholderDesignation}
                                    />
                                </div>
                                <Champ
                                    libelle={t.champPart}
                                    valeur={activite.part}
                                    poser={poserCellule(i, "part")}
                                    inputMode="decimal"
                                />
                                <Champ
                                    libelle={t.champEffectif}
                                    valeur={activite.effectif}
                                    poser={poserCellule(i, "effectif")}
                                    inputMode="numeric"
                                />
                            </div>
                            {r.activites.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => retirerLigne(i)}
                                    className="mt-3 text-xs text-gray-500 dark:text-lightBlue/70 hover:text-primary transition-colors duration-200"
                                >
                                    {t.retirerActivite}
                                </button>
                            )}
                        </div>
                    ))}
                </div>

                <button
                    type="button"
                    onClick={ajouterLigne}
                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:opacity-80 transition-opacity duration-200"
                >
                    <SolarIcon name="clipboard-check-linear" className="text-lg" />
                    {t.ajouterActivite}
                </button>

                <p
                    className={`text-sm mt-4 mb-0 ${
                        total === 100
                            ? "text-green-600 dark:text-green-400"
                            : "text-amber-600 dark:text-amber-400"
                    }`}
                >
                    {total === 100
                        ? t.totalJuste
                        : total < 100
                          ? remplir(t.totalFaux, { ecart })
                          : remplir(t.totalTrop, { ecart })}
                </p>

                <Avancer
                    pleineLargeur
                    disabled={activitesRemplies(r.activites).length === 0 || Boolean(refusDe("activites"))}
                    onClick={() => aller("contact")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "contact") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.contactTitre} aide={t.contactAide}>
                <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ libelle={t.champPrenoms} valeur={r.prenoms} poser={poser("prenoms")} />
                        <Champ libelle={t.champNom} valeur={r.nom} poser={poser("nom")} />
                    </div>
                    <Champ
                        libelle={t.champFonction}
                        aide={t.aideFonction}
                        valeur={r.fonction}
                        poser={poser("fonction")}
                    />
                    <Champ
                        type="email"
                        libelle={t.champMail}
                        valeur={r.mail}
                        poser={poser("mail")}
                        erreur={erreurDe("mail")}
                    />
                    <Champ
                        type="tel"
                        libelle={t.champTelephone}
                        aide={t.aideTelephone}
                        valeur={r.telephone}
                        poser={poser("telephone")}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("prenoms", "nom", "fonction", "mail") || Boolean(refusDe("mail"))}
                    onClick={() => aller("signature")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "signature") {
        return (
            <Ecran key={etape} {...commun} phase={2} onRetour={retour} titre={t.signatureTitre} aide={t.signatureAide}>
                <div className="space-y-5">
                    <Champ
                        libelle={t.champLieuSignature}
                        valeur={r.lieuDeSignature}
                        poser={poser("lieuDeSignature")}
                    />
                    <Champ
                        type="date"
                        libelle={t.champDateSignature}
                        valeur={r.dateDeSignature || jour}
                        poser={poser("dateDeSignature")}
                        erreur={erreurDe("dateDeSignature")}
                    />
                    <PadSignature
                        libelle={t.signatureDessin}
                        aide={t.signatureDessinAide}
                        effacer={t.effacer}
                        repriseLibelle={t.signatureReprise}
                        valeur={signature}
                        poser={poserSignature}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("lieuDeSignature")}
                    onClick={() => aller("resultat")}
                >
                    {t.voirDocument}
                </Avancer>
            </Ecran>
        );
    }

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            {!declaration ? (
                <div className="mt-4">
                    <div className="font-bold text-lg dark:text-white leading-snug">
                        {controle.manques.length ? t.manquesTitre : t.verifierTitre}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {controle.manques.length ? t.manquesAide : t.verifierAide}
                    </p>
                    <ul className="mt-4 space-y-2">
                        {controle.manques.map((cle) => (
                            <li key={cle} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-red-500/70" />
                                <span>{LIBELLES_MANQUES[lang]?.[cle] || LIBELLES_MANQUES.fr[cle]}</span>
                            </li>
                        ))}
                        {refusDuTableau.map((message) => (
                            <li key={message} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500/70" />
                                <span>{message}</span>
                            </li>
                        ))}
                        {(controle.invalides || [])
                            .filter((i) => i.champ !== "activites")
                            .map(({ champ, motif }) => (
                                <li key={`${champ}-${motif}`} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                    <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500/70" />
                                    <span>
                                        {LIBELLES_MANQUES[lang]?.[champ] || LIBELLES_MANQUES.fr[champ]}
                                        {" : "}
                                        {erreurDe(champ)}
                                    </span>
                                </li>
                            ))}
                    </ul>
                    <Avancer pleineLargeur onClick={retour}>
                        {t.retour}
                    </Avancer>
                </div>
            ) : (
                <div className="mt-4">
                    {(controle.alertes.some((a) => a.startsWith("regroupement")) ||
                        controle.alertes.includes("uneSeuleActivite")) && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-5 space-y-2">
                            {controle.alertes.some((a) => a.startsWith("regroupement")) && (
                                <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">
                                    {t.alerteRegroupement}
                                </p>
                            )}
                            {controle.alertes.includes("uneSeuleActivite") && (
                                <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">
                                    {t.alerteUneSeule}
                                </p>
                            )}
                        </div>
                    )}

                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.recapTitre}
                    </div>
                    <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 overflow-hidden mb-6">
                        <table className="w-full text-sm">
                            <tbody>
                                {recap.map(([titre, valeur]) => (
                                    <tr key={titre} className="border-b last:border-b-0 border-gray-200/70 dark:border-white/10">
                                        <td className="px-5 py-3 text-gray-500 dark:text-lightBlue/70 align-top w-2/5">{titre}</td>
                                        <td className="px-5 py-3 font-medium dark:text-white">{valeur}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.apercu}
                    </div>
                    <ApercuDocument document={declaration} signature={signature} />

                    <div className="mt-5 flex flex-col sm:flex-row gap-3">
                        <button
                            type="button"
                            onClick={telecharger}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#D04515] text-white font-semibold transition-opacity duration-200 hover:opacity-90"
                        >
                            <SolarIcon name="download-minimalistic-linear" className="text-lg" />
                            {t.telecharger}
                        </button>
                        <button
                            type="button"
                            onClick={copier}
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-gray-200/70 dark:border-white/10 text-gray-600 dark:text-lightBlue font-semibold transition-colors duration-200 hover:border-primary/40 hover:text-primary"
                        >
                            <SolarIcon name={copie ? "check-circle-linear" : "copy-linear"} className="text-lg" />
                            {copie ? t.copie : t.copier}
                        </button>
                    </div>

                    <div className="mt-8">
                        <div className="flex items-center gap-2.5 mb-4">
                            <SolarIcon name="clipboard-check-linear" className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60" />
                            <div className="font-bold dark:text-white leading-snug">{t.suiteTitre}</div>
                        </div>
                        <ul className="space-y-3">
                            {[
                                t.suite1,
                                remplir(t.suite2, { delai: DELAI_REPONSE_JOURS_OUVRES }),
                                t.suite3,
                            ].map((ligne) => (
                                <li key={ligne} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                    <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-white/20" />
                                    <span>{ligne}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mt-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                        <div className="flex items-center gap-2.5 mb-3">
                            <SolarIcon name="shield-linear" className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60" />
                            <div className="font-semibold dark:text-white leading-snug">{t.fondTitre}</div>
                        </div>
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">{t.fond}</p>
                    </div>

                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            )}
        </Resultat>
    );
}
